<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const DAYS = computed(() => [
  { key: 'mon', label: t('APPOINTMENTS.WORKING_HOURS.DAYS.MON') },
  { key: 'tue', label: t('APPOINTMENTS.WORKING_HOURS.DAYS.TUE') },
  { key: 'wed', label: t('APPOINTMENTS.WORKING_HOURS.DAYS.WED') },
  { key: 'thu', label: t('APPOINTMENTS.WORKING_HOURS.DAYS.THU') },
  { key: 'fri', label: t('APPOINTMENTS.WORKING_HOURS.DAYS.FRI') },
  { key: 'sat', label: t('APPOINTMENTS.WORKING_HOURS.DAYS.SAT') },
  { key: 'sun', label: t('APPOINTMENTS.WORKING_HOURS.DAYS.SUN') },
]);

const currentValue = computed(() => props.modelValue || {});

const isDayEnabled = key => {
  const windows = currentValue.value[key];
  return Array.isArray(windows) && windows.length > 0;
};

const getWindows = key => {
  return currentValue.value[key] || [];
};

const emitUpdate = newValue => {
  emit('update:modelValue', { ...newValue });
};

const toggleDay = (key, enabled) => {
  const updated = { ...currentValue.value };
  if (enabled) {
    updated[key] = [{ start: '08:00', end: '18:00' }];
  } else {
    delete updated[key];
  }
  emitUpdate(updated);
};

const addWindow = key => {
  const updated = { ...currentValue.value };
  const windows = [...(updated[key] || [])];
  windows.push({ start: '08:00', end: '18:00' });
  updated[key] = windows;
  emitUpdate(updated);
};

const removeWindow = (key, idx) => {
  const updated = { ...currentValue.value };
  const windows = [...(updated[key] || [])];
  windows.splice(idx, 1);
  if (windows.length === 0) {
    delete updated[key];
  } else {
    updated[key] = windows;
  }
  emitUpdate(updated);
};

const updateWindow = (key, idx, field, value) => {
  const updated = { ...currentValue.value };
  const windows = [...(updated[key] || [])];
  windows[idx] = { ...windows[idx], [field]: value };
  updated[key] = windows;
  emitUpdate(updated);
};
</script>

<template>
  <div class="space-y-3">
    <div
      v-for="day in DAYS"
      :key="day.key"
      class="flex items-start gap-3 py-2 border-b border-n-weak last:border-0"
    >
      <div class="w-28 flex items-center gap-2 pt-1">
        <input
          :id="`day-enabled-${day.key}`"
          type="checkbox"
          :checked="isDayEnabled(day.key)"
          class="rounded border-n-strong"
          @change="toggleDay(day.key, $event.target.checked)"
        />
        <label
          :for="`day-enabled-${day.key}`"
          class="text-sm font-medium text-n-slate-12 select-none cursor-pointer"
        >
          {{ day.label }}
        </label>
      </div>

      <div class="flex-1">
        <template v-if="isDayEnabled(day.key)">
          <div
            v-for="(window, idx) in getWindows(day.key)"
            :key="idx"
            class="flex items-center gap-2 mb-2"
          >
            <input
              type="time"
              :value="window.start"
              class="border border-n-strong rounded px-2 py-1 text-sm"
              @change="updateWindow(day.key, idx, 'start', $event.target.value)"
            />
            <span class="text-n-slate-11 text-sm">&ndash;</span>
            <input
              type="time"
              :value="window.end"
              class="border border-n-strong rounded px-2 py-1 text-sm"
              @change="updateWindow(day.key, idx, 'end', $event.target.value)"
            />
            <button
              type="button"
              class="text-n-ruby-9 hover:text-n-ruby-11 text-sm"
              :aria-label="t('APPOINTMENTS.WORKING_HOURS.REMOVE_WINDOW')"
              @click="removeWindow(day.key, idx)"
            >
              <span aria-hidden="true" class="i-lucide-x size-3.5" />
            </button>
          </div>
          <button
            type="button"
            class="text-xs text-n-brand hover:text-n-brand font-medium"
            @click="addWindow(day.key)"
          >
            + {{ t('APPOINTMENTS.WORKING_HOURS.ADD_WINDOW') }}
          </button>
        </template>
        <span v-else class="text-sm text-n-slate-10 italic">{{
          t('APPOINTMENTS.WORKING_HOURS.UNAVAILABLE')
        }}</span>
      </div>
    </div>
  </div>
</template>
